import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { saveToGlobalStore } from '../utils/globalAudioStore';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'https://taraanaya-prod-production.up.railway.app';

// Domain configuration mapping
const DOMAIN_CONFIG = {
  1: { key: 'health', name: 'Health & Longevity', color: '#22c55e', icon: '💚' },
  2: { key: 'career', name: 'Career & Success', color: '#3b82f6', icon: '💼' },
  3: { key: 'wealth', name: 'Wealth & Prosperity', color: '#f59e0b', icon: '💰' },
  4: { key: 'marriage', name: 'Marriage & Love', color: '#ec4899', icon: '💗' },
  5: { key: 'family', name: 'Family & Relations', color: '#a855f7', icon: '🏡' },
  6: { key: 'education', name: 'Education & Knowledge', color: '#6366f1', icon: '🎓' },
  7: { key: 'travel', name: 'Travel & Adventure', color: '#06b6d4', icon: '✈️' },
  8: { key: 'spiritual', name: 'Spiritual Growth', color: '#8b5cf6', icon: '🌅' },
  // Also support key-based access
  health: { key: 'health', name: 'Health & Longevity', color: '#22c55e', icon: '💚' },
  career: { key: 'career', name: 'Career & Success', color: '#3b82f6', icon: '💼' },
  wealth: { key: 'wealth', name: 'Wealth & Prosperity', color: '#f59e0b', icon: '💰' },
  marriage: { key: 'marriage', name: 'Marriage & Love', color: '#ec4899', icon: '💗' },
  family: { key: 'family', name: 'Family & Relations', color: '#a855f7', icon: '🏡' },
  education: { key: 'education', name: 'Education & Knowledge', color: '#6366f1', icon: '🎓' },
  travel: { key: 'travel', name: 'Travel & Adventure', color: '#06b6d4', icon: '✈️' },
  spiritual: { key: 'spiritual', name: 'Spiritual Growth', color: '#8b5cf6', icon: '🌅' },
};

const LoadingDomainReel = () => {
  const { domain } = useParams();
  const navigate = useNavigate();
  
  const [progress, setProgress] = useState(0);
  const [currentLanguage, setCurrentLanguage] = useState('');
  const [statusMessage, setStatusMessage] = useState('Preparing your journey...');
  const [isComplete, setIsComplete] = useState(false);
  const abortRef = useRef(false);
  
  // Get domain config
  const domainConfig = DOMAIN_CONFIG[domain] || DOMAIN_CONFIG.health;
  const domainKey = domainConfig.key;
  
  // Generate TTS for a single text chunk
  const generateTTS = async (text, language) => {
    try {
      const response = await fetch(`${API_URL}/api/tts/edge`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text, language })
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.success && result.audio) {
          // Create blob URL for immediate playback
          const blob = await (await fetch(`data:audio/mp3;base64,${result.audio}`)).blob();
          const audioUrl = URL.createObjectURL(blob);
          const dataUrl = `data:audio/mpeg;base64,${result.audio}`;
          
          // Save to global in-memory store for INSTANT access in FutureStoryExact/DomainReelsStory
          saveToGlobalStore(language, text, audioUrl, dataUrl);
          
          // Also save to IndexedDB for persistence across app restarts
          try {
            const { default: LocalAudioCache } = await import('../utils/localAudioCache');
            const userId = localStorage.getItem('audio_user_id') || localStorage.getItem('user_id') || 'anon';
            const idbKey = `fs_${language}_${text.substring(0, 40).replace(/\s+/g, '_')}`;
            await LocalAudioCache.saveAudioLocal(userId, 'future_story_chunk', idbKey, result.audio);
          } catch (_) {}
          
          return { success: true, audioUrl };
        }
        return { success: result.success, audioUrl: null };
      }
      return { success: false, audioUrl: null };
    } catch (e) {
      console.error('TTS error:', e);
      return { success: false, audioUrl: null };
    }
  };
  
  // Split text into TTS-friendly chunks
  const splitIntoChunks = (text, maxChars = 400) => {
    if (!text || text.length === 0) return [];
    
    const cleanText = text.replace(/[\u{1F300}-\u{1F9FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]|❤️|💼|📚|💰|💍|🌍|✨|🌟|🙏/gu, '').trim();
    
    if (cleanText.length <= maxChars) return [cleanText];
    
    const chunks = [];
    let currentChunk = '';
    
    // Split by sentences first
    const sentences = cleanText.split(/(?<=[.!?।])\s+/);
    
    for (const sentence of sentences) {
      if ((currentChunk + sentence).length > maxChars) {
        if (currentChunk.length > 50) chunks.push(currentChunk.trim());
        currentChunk = sentence;
      } else {
        currentChunk += (currentChunk ? ' ' : '') + sentence;
      }
    }
    if (currentChunk.length > 50) chunks.push(currentChunk.trim());
    
    return chunks;
  };
  
  useEffect(() => {
    abortRef.current = false;
    
    // Main audio generation function
    const generateAllAudio = async () => {
    console.log(`🎬 Starting pre-generation for domain: ${domainKey}`);
    
    // Get story data from localStorage
    const unifiedDataStr = localStorage.getItem('unified_story_data');
    if (!unifiedDataStr) {
      console.error('No unified story data found');
      setStatusMessage('No story data found. Generating...');
      navigate('/generating-story');
      return;
    }
    
    let unifiedData;
    try {
      unifiedData = JSON.parse(unifiedDataStr);
    } catch (e) {
      console.error('Failed to parse story data');
      navigate('/generating-story');
      return;
    }
    
    // Birth Details = Source of Truth for name
    const birthRaw = localStorage.getItem('birthDetails') || localStorage.getItem('birth_details');
    const birthParsed = birthRaw ? JSON.parse(birthRaw) : null;
    const userName = birthParsed?.name || unifiedData.user_info?.name || 'User';
    const userId = localStorage.getItem('audio_user_id') || `user_${Date.now()}`;
    
    // Get domain-specific content for each language
    const domainReels = unifiedData.future_story?.domain_reels || {};
    const mahadashas = unifiedData.future_story?.mahadashas || [];
    
    // Debug log available domains
    console.log(`📦 Checking domain_reels for ${domainKey}...`);
    console.log('   Available keys:', Object.keys(domainReels));
    
    const languages = ['hinglish', 'hindi', 'english'];
    const languageLabels = {
      hinglish: 'Hinglish',
      hindi: 'हिंदी',
      english: 'English'
    };
    
    // Check if multi-language structure exists
    const isMultiLang = domainReels.hinglish || domainReels.hindi || domainReels.english;
    
    // Check if content exists for this domain
    let hasDomainContent = false;
    if (isMultiLang) {
      for (const lang of languages) {
        const content = domainReels[lang]?.[domainKey] || '';
        if (content && content.length > 50) {
          hasDomainContent = true;
          console.log(`   ✅ ${lang} has ${content.length} chars for ${domainKey}`);
        } else {
          console.log(`   ⚠️ ${lang} missing/short content for ${domainKey}`);
        }
      }
    } else {
      const content = domainReels[domainKey] || '';
      hasDomainContent = content && content.length > 50;
    }
    
    if (!hasDomainContent) {
      console.warn(`⚠️ No narration content for ${domainKey} - user may need to regenerate story`);
      setStatusMessage(`No ${domainConfig.name} content found. Redirecting...`);
      // Still navigate but the fallback will be used
      setTimeout(() => {
        navigate(`/future-story/${domain}`, { replace: true, state: { preloaded: false } });
      }, 2000);
      return;
    }
    
    let totalChunks = 0;
    let completedChunks = 0;
    
    // Calculate total chunks first
    const allLanguageChunks = {};
    for (const lang of languages) {
      let domainContent = '';
      if (isMultiLang) {
        domainContent = domainReels[lang]?.[domainKey] || '';
      } else {
        domainContent = domainReels[domainKey] || '';
      }
      
      // Generate intro based on language
      let intro = '';
      if (lang === 'hindi') {
        intro = `नमस्ते ${userName}! मैं आपको ${domainConfig.name} की भविष्य की पूरी कहानी सुनाने जा रहा हूं। आपके आगे ${mahadashas.length} महादशाएं हैं।`;
      } else if (lang === 'english') {
        intro = `Hello ${userName}! I am going to tell you the complete future story of ${domainConfig.name}. You have ${mahadashas.length} Mahadashas ahead.`;
      } else {
        intro = `Namaste ${userName}! Main aapko ${domainConfig.name} ki bhavishya ki poori kahani sunane ja raha hoon. Aapke aage ${mahadashas.length} Mahadashas hain.`;
      }
      
      const fullText = intro + '\n\n' + domainContent;
      const chunks = splitIntoChunks(fullText);
      allLanguageChunks[lang] = chunks;
      totalChunks += chunks.length;
    }
    
    console.log(`📊 Total chunks to generate: ${totalChunks} across 3 languages`);
    setStatusMessage(`Preparing ${totalChunks} audio segments...`);
    
    if (totalChunks === 0) {
      // No content to generate - still navigate
      console.log('⚠️ No content found, navigating directly');
      navigate(`/future-story/${domain}`, { replace: true });
      return;
    }
    
    // Store audio URLs for each language
    const audioUrlsCache = { hindi: {}, hinglish: {}, english: {} };
    
    // Generate audio for each language
    for (const lang of languages) {
      if (abortRef.current) break;
      
      const chunks = allLanguageChunks[lang];
      setCurrentLanguage(languageLabels[lang]);
      setStatusMessage(`Generating ${languageLabels[lang]} narration...`);
      
      for (let i = 0; i < chunks.length; i++) {
        if (abortRef.current) break;
        
        const chunk = chunks[i];
        console.log(`🎙️ [${lang}] Generating chunk ${i + 1}/${chunks.length}: ${chunk.substring(0, 50)}...`);
        
        const result = await generateTTS(chunk, lang);
        
        if (result.success) {
          completedChunks++;
          const percent = Math.round((completedChunks / totalChunks) * 100);
          setProgress(percent);
          
          // Store the audio URL for this chunk
          if (result.audioUrl) {
            audioUrlsCache[lang][i] = result.audioUrl;
          }
          
          console.log(`✅ [${lang}] Chunk ${i + 1} complete (${percent}%)`);
        } else {
          console.warn(`⚠️ [${lang}] Chunk ${i + 1} failed, continuing...`);
          completedChunks++;
          setProgress(Math.round((completedChunks / totalChunks) * 100));
        }
      }
    }
    
    // Store audio URLs in localStorage for DomainReelsStory to use
    const cacheKey = `domain_audio_cache_${domainKey}`;
    localStorage.setItem(cacheKey, JSON.stringify({
      audioUrls: audioUrlsCache,
      chunks: allLanguageChunks,
      timestamp: Date.now()
    }));
    console.log(`💾 Stored audio URLs cache for ${domainKey}`);
    
    // Complete!
    setProgress(100);
    setIsComplete(true);
    setStatusMessage('Ready! Starting your journey...');
    
    // Navigate to the actual domain reels page
    setTimeout(() => {
      if (!abortRef.current) {
        navigate(`/future-story/${domain}`, { replace: true, state: { preloaded: true } });
      }
    }, 1000);
  };
  
    // Start generation on mount
    generateAllAudio();
    
    return () => {
      abortRef.current = true;
    };
  }, [domain, domainKey, domainConfig.name, navigate]);
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0a1628] via-[#0d1a30] to-[#0a1628] flex flex-col items-center justify-center p-4">
      {/* Cosmic background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(1px 1px at 20px 30px, rgba(255,255,255,0.5), transparent),
                           radial-gradient(1.5px 1.5px at 50px 160px, rgba(255,255,255,0.6), transparent),
                           radial-gradient(1px 1px at 90px 40px, rgba(255,255,255,0.3), transparent)`,
          backgroundSize: '100vw 100vh'
        }}/>
        <div 
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] rounded-full blur-[100px]"
          style={{ backgroundColor: `${domainConfig.color}20` }}
        />
      </div>
      
      <div className="relative z-10 flex flex-col items-center max-w-sm w-full">
        {/* Domain Icon */}
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.5 }}
          className="text-6xl mb-6"
        >
          {domainConfig.icon}
        </motion.div>
        
        {/* Domain Name */}
        <h1 className="text-xl font-bold text-white mb-2 text-center">
          {domainConfig.name}
        </h1>
        
        {/* Status Message */}
        <p className="text-white/60 text-sm mb-8 text-center">
          {statusMessage}
        </p>
        
        {/* Progress Bar */}
        <div className="w-full bg-white/10 rounded-full h-3 mb-4 overflow-hidden">
          <motion.div
            className="h-full rounded-full"
            style={{ backgroundColor: domainConfig.color }}
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.3 }}
          />
        </div>
        
        {/* Progress Text */}
        <div className="flex justify-between w-full text-xs text-white/50 mb-4">
          <span>{currentLanguage || 'Initializing...'}</span>
          <span>{progress}%</span>
        </div>
        
        {/* Language Pills */}
        <div className="flex gap-2 mt-4">
          {['Hindi', 'Hinglish', 'English'].map((lang) => (
            <div
              key={lang}
              className={`px-3 py-1 rounded-full text-xs ${
                currentLanguage === lang || 
                (currentLanguage === 'हिंदी' && lang === 'Hindi')
                  ? 'bg-white/20 text-white'
                  : 'bg-white/5 text-white/40'
              }`}
            >
              {lang}
            </div>
          ))}
        </div>
        
        {/* Sage Image */}
        <motion.div
          className="mt-8"
          animate={!isComplete ? { 
            y: [0, -5, 0],
          } : {}}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <img 
            src="/sage-final.png"
            alt="Sage"
            className="w-24 h-auto opacity-80"
            style={{ filter: `drop-shadow(0 0 20px ${domainConfig.color}50)` }}
          />
        </motion.div>
        
        {/* Loading indicator text */}
        <p className="text-white/40 text-xs mt-4 text-center">
          {isComplete 
            ? '✨ Your narration is ready!'
            : 'Pre-generating audio for seamless playback...'}
        </p>
      </div>
    </div>
  );
};

export default LoadingDomainReel;
